import { Cancel, CheckCircle, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  getPaymentRequestPassiveReversePreview,
  reversePaymentRequestPassive,
} from 'lib/api/paymentRequestPassives';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface ReverseConfirmationProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  paymentRequestId: string;
  setSuccess: (message: string) => void;
  setFailure: (message: string) => void;
  setUp: () => void;
}

interface PreviewData {
  paymentRequestPassive: {
    id: number;
    amount: number;
    createdAt: string;
    executedAt: string;
    status: string;
  };
  payOuts: Array<{
    id: number;
    amount: number;
    status: string;
    executedAt: string;
    bankAccountOriginId: number;
    bankAccountDestinationId: number;
  }>;
  documentsToPay: Array<{
    id: number;
    documentId: string;
    amount: number;
    document: {
      id: string;
      folio: string;
      business: {
        identifier: string;
        name: string;
      };
    };
  }>;
  documentsApplied: Array<{
    id: number;
    documentId: string;
    amount: number;
    paidAt: string;
    document: {
      id: string;
      folio: string;
      business: {
        identifier: string;
        name: string;
      };
    };
  }>;
  summary: {
    totalPayOuts: number;
    totalPayOutsAmount: number;
    executedPayOuts: number;
    executedPayOutsAmount: number;
    pendingPayOuts: number;
    pendingPayOutsAmount: number;
    documentsToPayCount: number;
    documentsToPayAmount: number;
    documentsAppliedCount: number;
    documentsAppliedAmount: number;
    willDeletePaymentRequest: boolean;
    willRevertPayOuts: boolean;
    willDeleteConciliations: boolean;
    willRevertDocuments: boolean;
  };
  warnings: string[];
  canReverse: boolean;
}

const ReverseConfirmation: React.FC<ReverseConfirmationProps> = ({
  open,
  setOpen,
  paymentRequestId,
  setSuccess,
  setFailure,
  setUp,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [canReverse, setCanReverse] = useState(false);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (open && paymentRequestId) {
      fetchPreview();
    }
  }, [open, paymentRequestId]);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const data = await getPaymentRequestPassiveReversePreview(
        dispatch,
        paymentRequestId,
      );
      setPreviewData(data);
      setCanReverse(data.canReverse);
    } catch (error) {
      console.error('Error al obtener preview:', error);
      setFailure('Error al obtener la información de reversión');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setExecuting(true);
    try {
      await reversePaymentRequestPassive(dispatch, paymentRequestId);
      setSuccess('Reversión completada exitosamente');
      setUp();
      setOpen(false);
    } catch (error) {
      console.error('Error al ejecutar reversión:', error);
      setFailure('Error al ejecutar la reversión');
    } finally {
      setExecuting(false);
    }
  };

  const handleClose = () => {
    if (!executing) {
      setOpen(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CenteredCircularProgress type="dialog" />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!previewData) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Confirmar Reversión de Solicitud de Pago
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500}>
            Esta acción eliminará completamente la solicitud de pago y todos sus
            registros relacionados. Esta acción no se puede deshacer.
          </Typography>
        </Alert>

        {/* Resumen */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Resumen de la Reversión
          </Typography>

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Pagos a revertir
              </Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {previewData.summary.totalPayOuts}{' '}
                {previewData.summary.totalPayOuts === 1 ? 'pago' : 'pagos'} por{' '}
                {formatMoney(previewData.summary.totalPayOutsAmount)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Documentos a eliminar
              </Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {previewData.summary.documentsToPayCount +
                  previewData.summary.documentsAppliedCount}{' '}
                {previewData.summary.documentsToPayCount +
                  previewData.summary.documentsAppliedCount ===
                1
                  ? 'documento'
                  : 'documentos'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Advertencias */}
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Advertencias
        </Typography>

        <List dense>
          {previewData.warnings.map((warning, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Warning color="warning" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={warning}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Detalles adicionales */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Solicitud ID:</strong> {previewData.paymentRequestPassive.id}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Monto total:</strong>{' '}
          {formatMoney(previewData.paymentRequestPassive.amount)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Fecha de ejecución:</strong>{' '}
          {new Date(
            previewData.paymentRequestPassive.executedAt,
          ).toLocaleDateString()}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          onClick={handleClose}
          color="secondary"
          disabled={executing}
          startIcon={<Cancel />}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleConfirm}
          color="error"
          disabled={executing || !canReverse}
          startIcon={executing ? undefined : <CheckCircle />}
        >
          {executing ? 'Ejecutando...' : 'Confirmar Reversión'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReverseConfirmation;
